import React, { useEffect, useState } from 'react';
import { rolloverApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { RefreshCw, Loader2, Eye, Play, Undo2 } from 'lucide-react';
import { currentLeaveYear, leaveYearLabel } from '../lib/leaveYear';

// `startMonth` is the company's leave-year start month. ⚠️ The default is the NEXT LEAVE
// year, numbered by the year it starts in (DESIGN-leave-years.md) — it used to be next
// CALENDAR year, which at an April company in January is the year after next.
const RolloverPanel = ({ startMonth = 1 }) => {
  const nextYear = currentLeaveYear(startMonth) + 1;
  const [year, setYear] = useState(nextYear);
  useEffect(() => { setYear(currentLeaveYear(startMonth) + 1); }, [startMonth]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [data, setData] = useState(null);

  const doPreview = async () => {
    setLoading(true);
    try {
      const r = await rolloverApi.preview(year);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to preview rollover');
    } finally {
      setLoading(false);
    }
  };

  const doRun = async () => {
    if (!window.confirm(`Run the year-end rollover for ${leaveYearLabel(startMonth, year)}? This creates ${leaveYearLabel(startMonth, year)} allowances for everyone who doesn't already have one. If that leave year hasn't started yet, unused days are carried over on the day it starts, from what is actually left then. Existing allowances are left untouched.`)) return;
    setRunning(true);
    try {
      const r = await rolloverApi.run(year);
      toast.success(`Rollover complete — ${r.data.created} created, ${r.data.skipped} already existed.`);
      await doPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to run rollover');
    } finally {
      setRunning(false);
    }
  };

  const doUndo = async () => {
    if (!window.confirm(`Undo the ${year} rollover? This deletes the ${year} allowance rows that haven't been booked into yet. Rows where leave has already been taken/pending are kept.`)) return;
    setRunning(true);
    try {
      const r = await rolloverApi.undo(year);
      toast.success(`Undone — ${r.data.deleted} deleted${r.data.skipped ? `, ${r.data.skipped} kept (already booked)` : ''}.`);
      await doPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to undo rollover');
    } finally {
      setRunning(false);
    }
  };

  const rows = data?.rows || [];
  const toCreate = rows.filter((r) => !r.already_exists).length;
  const existing = rows.filter((r) => r.already_exists).length;

  return (
    <div className="card-hive p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
          <RefreshCw className="w-5 h-5 text-emerald-500" />
        </div>
        <div>
          <h2 className="font-heading font-bold text-lg">Year-end rollover</h2>
          <p className="text-sm text-muted-foreground">Create next year's allowances, carrying over unused days and refreshing the service bonus. Preview before you run.</p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <Label>Leave year{startMonth !== 1 ? ' (the year it starts in)' : ''}</Label>
          <Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value) || nextYear)} className="input-hive mt-1 w-32" />
        </div>
        <Button variant="outline" onClick={doPreview} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />} Preview
        </Button>
        {data && data.annual_type_found && (
          <Button className="btn-primary" onClick={doRun} disabled={running || toCreate === 0}>
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            Run rollover{toCreate > 0 ? ` (${toCreate})` : ''}
          </Button>
        )}
        {data && data.annual_type_found && existing > 0 && (
          <Button variant="ghost" onClick={doUndo} disabled={running} className="text-red-600 hover:text-red-700 hover:bg-red-500/10">
            <Undo2 className="w-4 h-4 mr-2" /> Undo ({existing})
          </Button>
        )}
      </div>

      {data && !data.annual_type_found && (
        <p className="text-sm text-amber-600">No “Annual Leave” type found for this company, so there's nothing to roll over.</p>
      )}

      {data && data.annual_type_found && (
        <>
          <div className="text-xs text-muted-foreground mb-2">
            Carry-over: <span className="font-medium text-foreground">{data.carry_over_enabled ? `enabled${data.carry_over_max_days ? ` (max ${data.carry_over_max_days} days)` : ''}` : 'disabled'}</span>
            {' · '}{rows.length} employee{rows.length === 1 ? '' : 's'}{toCreate > 0 ? ` · ${toCreate} to create` : ' · all up to date'}
          </div>
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/40 sticky top-0">
                  <tr className="text-left">
                    <th className="p-2.5 font-medium">Employee</th>
                    <th className="p-2.5 font-medium text-right">Prev. remaining</th>
                    <th className="p-2.5 font-medium text-right">Carried over</th>
                    <th className="p-2.5 font-medium text-right">New entitlement</th>
                    <th className="p-2.5 font-medium text-right">New total</th>
                    <th className="p-2.5 font-medium text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.user_id} className="border-t border-border/50">
                      <td className="p-2.5">{r.name}</td>
                      <td className="p-2.5 text-right text-muted-foreground">{r.prev_remaining}</td>
                      <td className="p-2.5 text-right">{r.carried_over > 0 ? <span className="text-emerald-600 font-medium">+{r.carried_over}</span> : '0'}</td>
                      <td className="p-2.5 text-right">{r.new_entitlement}</td>
                      <td className="p-2.5 text-right font-semibold">{r.new_total}</td>
                      <td className="p-2.5 text-right">
                        {r.already_exists
                          ? <span className="text-[11px] text-muted-foreground">exists</span>
                          : <span className="text-[11px] text-emerald-600 bg-emerald-500/10 px-1.5 py-0.5 rounded-full">will create</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">Rollover is safe to run repeatedly — it only creates missing rows and never changes days already booked. Carry-over into a leave year that hasn't started yet is added on the day it starts.</p>
        </>
      )}
    </div>
  );
};

export default RolloverPanel;
